import React from 'react';
import { Globe } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';

/**
 * Website section landing page. Eventually houses everything the operator
 * needs to control their public-facing facility website that we host
 * (hosted as per-facility Vercel deploys).
 *
 * Planned tabs/features (Phase 8.7 of the TDD):
 *   - Page editor (Home, About, Contact, Size Guide, FAQ — operator
 *     edits the marketing copy WYSIWYG-style)
 *   - Photo gallery (drag-and-drop, alt text, ordering)
 *   - SEO settings (GA4 measurement ID, GSC verification, sitemap)
 *   - Domain config (current DNS provider, A/CNAME records to set,
 *     SSL status, redirects from old URLs)
 *   - Lead-capture form settings (which fields, which email notifies)
 *   - Public Rent button URL / mySMS360 subdomain link
 *
 * For now: a placeholder so the nav item resolves to something useful
 * (not 404), with operator-facing context about what the section will
 * do once shipped.
 */
const WebsiteSection = () => {
  const { currentFacility } = useFacility();

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
          <Globe className="h-5 w-5 text-slate-400" />
          Website
        </h1>
        <p className="text-[13px] text-slate-400 mt-0.5">
          Manage your facility's public-facing website
          {currentFacility?.website_domain && (
            <> at <span className="text-slate-600">{currentFacility.website_domain}</span></>
          )}
          .
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-8 max-w-3xl">
        <p className="text-[13px] uppercase tracking-wide text-slate-400 mb-3">
          Coming soon
        </p>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          A dedicated control panel for your facility website.
        </h2>
        <p className="text-[14px] text-slate-700 leading-relaxed mb-6">
          mySMS360 hosts a free public website for every facility
          (served at {'{facility}'}.com). Today those sites are
          edited via git pushes by the mySMS360 team. This section will
          eventually let operators edit them themselves &mdash; no code,
          no support tickets.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: 'Page editor',
              desc: 'WYSIWYG edits to Home, About, Contact, Size Guide, FAQ.',
            },
            {
              title: 'Photos & gallery',
              desc: 'Drag-and-drop facility photos; reorder; set alt text.',
            },
            {
              title: 'SEO settings',
              desc: 'GA4 measurement ID, Google Search Console verification, sitemap.',
            },
            {
              title: 'Domain & DNS',
              desc: 'Connect your domain (Namecheap, GoDaddy, etc.). We handle SSL.',
            },
            {
              title: 'Lead form',
              desc: 'Choose which fields, where notifications land.',
            },
            {
              title: 'Rent button',
              desc: 'Configure where "Rent Now" sends visitors (mySMS360, or external).',
            },
          ].map((f) => (
            <div
              key={f.title}
              className="rounded-lg border border-slate-200 bg-slate-50 p-4"
            >
              <h3 className="text-[13px] font-semibold text-slate-900 mb-1">
                {f.title}
              </h3>
              <p className="text-[12px] text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>

        <p className="text-[12px] text-slate-400 mt-6">
          Need a website change today? Email{' '}
          <a
            href="mailto:support@mysms360.com"
            className="text-blue-600 hover:underline"
          >
            support@mysms360.com
          </a>{' '}
          and we'll handle it.
        </p>
      </div>
    </div>
  );
};

export default WebsiteSection;
